import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Shield, Scan, Lock, Brain, AlertTriangle, BookOpen, CreditCard, LogOut, BarChart3, TrendingUp, Activity, ChevronRight, Menu, X, Cpu, Crosshair } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth, API } from "@/App";
import axios from "axios";
import { ThreatGlobe } from '@/components/globe/ThreatGlobe';

const SIDEBAR_ITEMS = [
  { icon: BarChart3, label: "Overview", path: "/dashboard" },
  { icon: Scan, label: "Breach Scanner", path: "/tools/breach-checker" },
  { icon: Lock, label: "Password Analyzer", path: "/tools/password-analyzer" },
  { icon: AlertTriangle, label: "Threat Intel", path: "/threats" },
  { icon: Brain, label: "AI Chat", path: "/chat" },
  { icon: Cpu, label: "Marketplace", path: "/marketplace" },
  { icon: Activity, label: "Agent Runtime", path: "/runtime" },
  { icon: BookOpen, label: "Education", path: "/education" },
  { icon: CreditCard, label: "Pricing", path: "/pricing" },
];

const FOUNDER_ITEMS = [
  { icon: BookOpen, label: "Patent Draft", path: "/patent-draft" },
  { icon: Crosshair, label: "Founders Cockpit", path: "/founders-cockpit" },
];

export function DashboardLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-60 bg-zinc-900 border-r border-zinc-800 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 flex items-center px-5 border-b border-zinc-800">
          <Link to="/" className="flex items-center gap-2" data-testid="sidebar-logo">
            <Shield className="w-6 h-6 text-blue-500" strokeWidth={1.5} />
            <span className="text-lg font-black tracking-tighter" style={{ fontFamily: 'var(--font-heading)' }}>ZYBERCURE</span>
          </Link>
        </div>
        <nav className="p-3 space-y-1">
          {(user?.role === "founder" ? [...SIDEBAR_ITEMS, ...FOUNDER_ITEMS] : SIDEBAR_ITEMS).map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  active ? 'bg-blue-600/10 text-blue-400 border border-blue-600/20' : 'text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800'
                }`}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-zinc-800">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-sm bg-zinc-800 flex items-center justify-center text-xs font-bold text-blue-400">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-zinc-50 truncate">{user?.name || "User"}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">{user?.plan || "free"} plan</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 w-full text-sm text-zinc-500 hover:text-red-400 transition-colors rounded-sm hover:bg-zinc-800"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 w-full lg:ml-60">
        <header className="h-14 glass border-b border-white/5 sticky top-0 z-30 flex items-center px-6 gap-4">
          <button className="lg:hidden text-zinc-400" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-base font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }} data-testid="page-title">{title}</h1>
        </header>
        <main className="p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const headers = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const resp = await axios.get(`${API}/dashboard/stats`, { headers, withCredentials: true });
        setStats(resp.data);
      } catch (err) {
        console.error("Failed to load stats", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const score = stats?.security_score || 0;
  const scoreColor = score >= 80 ? "text-emerald-400" : score >= 50 ? "text-amber-400" : "text-red-400";
  const progressColor = score >= 80 ? "bg-emerald-500" : score >= 50 ? "bg-amber-500" : "bg-red-500";

  return (
    <DashboardLayout title="Security Overview">
      <div className="space-y-6" data-testid="dashboard-content">
        <ThreatGlobe />
        {/* Welcome + Score */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 trace-border rounded-sm bg-zinc-900 p-6">
            <h2 className="text-xl font-bold tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              Welcome back, {stats?.user_name || user?.name || "Operator"}
            </h2>
            <p className="text-sm text-zinc-400 mb-4">Your security posture at a glance.</p>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: "Total Scans", value: stats?.total_scans || 0, icon: Scan },
                { label: "Passwords Checked", value: stats?.passwords_checked || 0, icon: Lock },
                { label: "AI Consultations", value: stats?.ai_consultations || 0, icon: Brain },
              ].map((item) => (
                <div key={item.label} className="bg-zinc-800/50 rounded-sm p-4 border border-zinc-800" data-testid={`stat-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <item.icon className="w-4 h-4 text-zinc-500 mb-2" strokeWidth={1.5} />
                  <div className="text-2xl font-black tracking-tighter text-zinc-50" style={{ fontFamily: 'var(--font-heading)' }}>{item.value}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mt-1">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="trace-border rounded-sm bg-zinc-900 p-6 flex flex-col items-center justify-center" data-testid="security-score-widget">
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">Security Score</span>
            <div className={`text-5xl font-black tracking-tighter ${scoreColor}`} style={{ fontFamily: 'var(--font-heading)' }}>
              {loading ? "—" : score}
            </div>
            <span className="text-xs text-zinc-500 mt-1">/ 100</span>
            <div className="w-full mt-4">
              <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className={`h-full ${progressColor} transition-all duration-1000`} style={{ width: `${score}%` }} />
              </div>
            </div>
            <p className="text-xs text-zinc-400 mt-3 text-center">Use more tools to improve your score</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h3 className="text-sm font-semibold tracking-tight mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { icon: Scan, label: "Scan for Breaches", path: "/tools/breach-checker", color: "blue" },
              { icon: Lock, label: "Analyze Password", path: "/tools/password-analyzer", color: "emerald" },
              { icon: AlertTriangle, label: "View Threats", path: "/threats", color: "amber" },
              { icon: Brain, label: "Ask ZyberGuard AI", path: "/chat", color: "purple" },
              { icon: Cpu, label: "Agent Marketplace", path: "/marketplace", color: "cyan" },
            ].map((action) => (
              <Link
                key={action.path}
                to={action.path}
                className="trace-border rounded-sm bg-zinc-900 p-4 group hover:scale-[1.01] transition-all duration-200 flex items-center gap-3"
                data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className={`w-9 h-9 rounded-sm flex items-center justify-center bg-${action.color}-500/10`}>
                  <action.icon className={`w-4 h-4 text-${action.color}-500`} strokeWidth={1.5} />
                </div>
                <span className="text-sm text-zinc-300 group-hover:text-zinc-50 transition-colors">{action.label}</span>
                <ChevronRight className="w-4 h-4 text-zinc-600 ml-auto" />
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="trace-border rounded-sm bg-zinc-900 p-6" data-testid="recent-activity">
          <h3 className="text-sm font-semibold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Recent Activity</h3>
          {stats?.recent_scans?.length > 0 ? (
            <div className="space-y-2">
              {stats.recent_scans.slice(0, 3).map((scan, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                  <div className="flex items-center gap-3">
                    <Scan className="w-4 h-4 text-blue-500" strokeWidth={1.5} />
                    <div>
                      <div className="text-sm text-zinc-300">{scan.email_checked}</div>
                      <div className="text-xs text-zinc-600 font-mono">{scan.scan_id}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-sm ${
                    scan.result?.risk_level === 'low' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                    scan.result?.risk_level === 'critical' || scan.result?.risk_level === 'high' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                    'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                  }`}>{scan.result?.risk_level || 'scanned'}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-600 text-center py-4">No scans yet. Run your first breach scan to get started.</p>
          )}
        </div>

        {/* Plan Info */}
        {stats?.plan === "free" && (
          <div className="trace-border rounded-sm bg-zinc-900 p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="upgrade-banner">
            <div>
              <h3 className="text-base font-semibold tracking-tight text-zinc-50" style={{ fontFamily: 'var(--font-heading)' }}>Upgrade to Pro</h3>
              <p className="text-sm text-zinc-400 mt-1">Unlock unlimited scans, AI chat, threat intelligence, and dark web monitoring.</p>
            </div>
            <Link to="/pricing">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-semibold" data-testid="upgrade-btn">
                View Plans <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        )}

        {/* Security Tips */}
        <div className="trace-border rounded-sm bg-zinc-900 p-6" data-testid="security-tips">
          <h3 className="text-sm font-semibold tracking-tight mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Security Tips</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { tip: "Enable 2FA on all critical accounts", status: "high" },
              { tip: "Review app permissions quarterly", status: "medium" },
              { tip: "Use a password manager for unique credentials", status: "high" },
              { tip: "Monitor your credit report for unauthorized activity", status: "low" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${item.status === 'high' ? 'bg-red-500' : item.status === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                <span className="text-zinc-400">{item.tip}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
